import React, { useEffect, useState } from 'react';
import './AdminTeacherForm.scss';
import images from '../../../assets/images';
import { useForm, SubmitHandler, FormProvider, Resolver } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import ToasterWithMessage from '../../../components/toaster';
import ButtonStyle from '../../../components/ButtonStyle/ButtonStyle';
import InputField from '../../../components/InputField/InputField';
import SubjectsInput from './SubjectsInput';
import * as yup from 'yup';
import { messages, patterns } from "../../../utils/constants";
import { useMutation } from "@apollo/client";
import { CREATE_TEACHERS } from "../api/AdminTeachersQuery";
import FileUpload from '../../../components/Popups/FileUpload';
import { DELETE_TEACHER, EDIT_TEACHER } from '../../AdminDashboard/api/AdminDashboardQuery';
import { getDecryptedDataWithSecretKey, getScreenDimensions } from '../../../utils/helper';
import ConfirmationPopup from '../../../components/ConfirmationPopup/ConfirmationPopup';

interface propsType {
    setDisplayForm: any;
    displayForm: boolean;
    setClickAction?: any;
    clickAction?: any;
    teacherByIdDetails?: any;
    setisFullView: (value: boolean) => void;
    isFullView: boolean;
}

interface Subject {
    subject: string;
    isPrimary: boolean;
}

interface IFormInput {
    teacherName: string;
    subjects: Subject[];
    username: string;
    password: string;
}

const schema = yup.object().shape({
    teacherName: yup
        .string()
        .required(messages?.name)
        // .matches(patterns?.alphabetSpace, messages?.validName)
        .max(100, 'Name should not exceed 100 characters'),
    subjects: yup.array().of(
        yup.lazy((value) => {
            const { isPrimary } = value;
            return isPrimary
                ? yup.object().shape({
                    isPrimary: yup.boolean(),
                    subject: yup.string()
                        .required(messages?.subject)
                        // .matches(patterns?.alphabetSpace, messages?.validSubject)
                        .max(100, 'Subject should not exceed 100 characters')
                        // .lowercase(),
                })
                : yup.object().shape({
                    isPrimary: yup.boolean(),
                    subject: yup.string()
                        // .matches(/^[A-Za-z\s!@#$%^&*(),.?":{}|<>_-]*$/, { excludeEmptyString: true }) // Allows special characters, alphabets & spaces
                        .max(100, 'Subject should not exceed 100 characters')
                        // .lowercase(),
                });
        })
    ).min(1, 'At least one subject is required'),
    username: yup
        .string()
        .strict()
        .trim(messages.invalidUsername)
        .required(messages.username)
        .matches(patterns.username, messages.validUsername)
        .matches(patterns.usernameStartWithAlphabets, messages.usernameStartWithAlphabets)
        .matches(patterns.consecutiveDotsDashesUnderscores, messages.noConsecutiveDotsDashesUnderscores)
        .min(6, messages.usernameMinLength)
        .max(50, messages.usernameMaxLength)
        .test('not-only-numbers', messages.invalidUsername, value => {
            if (value) {
                return !patterns.onlyNumbers.test(value);
            }
            return true;
        }),
    password: yup
        .string()
        .strict()
        .trim(messages.invalidPassword)
        .required(messages.password)
        .matches(patterns?.password, messages?.validPassword)
        .min(8, messages.passwordMinLength)
        .max(30, messages.passwordMaxLength)
        // .matches(patterns?.upperCasePassword, messages?.passwordOneUpperCase)
        // .matches(patterns?.lowerCasePassword, messages?.passwordOneLowerCase)
        // .matches(patterns?.oneNumbers, messages?.passwordOneNumber)
        // .matches(patterns?.oneSpecialCharacter, messages?.passwordOneSpecialCharater),
});

const AdminTeacherForm = ({ setDisplayForm, displayForm, setClickAction, clickAction, teacherByIdDetails, setisFullView, isFullView }: propsType) => {
    const { success, error } = ToasterWithMessage();
    const { backArrow, solidinterfaceplus, User, teacherDeleteIcon, DeleteGif, deleteIcon } = images;
    const [profileImageURL, setProfileImageURL] = useState<string>();
    const [ImageURL, setImageURL] = useState<string>();
    const [isUploadOpen, setIsUploadOpen] = useState(false);
    const [openConfirmationPopup, setOpenConfirmationPopup] = useState<boolean>(false);
    const baseUrl = process.env.REACT_APP_FRONTEND_URL;
    const { isMobileScreen } = getScreenDimensions();
    const methods = useForm<IFormInput>({
        resolver: yupResolver(schema) as Resolver<IFormInput>,
        defaultValues: {
            teacherName: '',
            subjects: [
                { subject: '', isPrimary: true },
                { subject: '', isPrimary: false },
                { subject: '', isPrimary: false },
                { subject: '', isPrimary: false },
                { subject: '', isPrimary: false },
            ],
            username: '',
            password: '',
        },
    });
    const [editTeacher, { loading: editTeacherLoading }] = useMutation(EDIT_TEACHER, {
        fetchPolicy: "no-cache",
        onCompleted: (data) => {
            success("Teacher updated successfully")
            setDisplayForm(!displayForm);
            setisFullView(!isFullView);
            setClickAction('');
            reset();
        },
        onError: (err) => {
            error(err?.message);
        }
    });

    const [deleteTeacher, { loading: deleteTeacherLoading }] = useMutation(DELETE_TEACHER, {
        fetchPolicy: "no-cache",
        onCompleted: (data) => {
            success("Teacher deleted successfully")
            setDisplayForm(!displayForm);
            setisFullView(!isFullView);
            setClickAction('');
            reset();
        },
        onError: (err) => {
            error(err?.message);
        }
    });

    const [createTeachers, { loading }] = useMutation(CREATE_TEACHERS, {
        fetchPolicy: "no-cache",
        onCompleted: (data) => {
            success("Teacher added successfully")
            setDisplayForm(!displayForm);
            setisFullView(!isFullView);
            setClickAction('');
            reset();
        },
        onError: (err) => {
            if ( err?.message === "Cognito Teacher User Creation Error: User already exists " ) {
                error("User already exists");
            }
            else {
                error(err?.message);
            }
        }
    });

    function areValuesDifferent(data: any) {
        const initialValues = {
            teacherName: teacherByIdDetails?.teacherByTId?.tName,
            subjects: [
                { subject: teacherByIdDetails?.teacherByTId?.mainSubject?.nodes?.[0]?.subjectBySjId?.sjOriginalName, isPrimary: true },
                { subject: teacherByIdDetails?.teacherByTId?.NonPrimarySubjects?.nodes?.[0]?.subjectBySjId?.sjOriginalName || '', isPrimary: false },
                { subject: teacherByIdDetails?.teacherByTId?.NonPrimarySubjects?.nodes?.[1]?.subjectBySjId?.sjOriginalName || '', isPrimary: false },
                { subject: teacherByIdDetails?.teacherByTId?.NonPrimarySubjects?.nodes?.[2]?.subjectBySjId?.sjOriginalName || '', isPrimary: false },
                { subject: teacherByIdDetails?.teacherByTId?.NonPrimarySubjects?.nodes?.[3]?.subjectBySjId?.sjOriginalName || '', isPrimary: false },
            ],
            username: teacherByIdDetails?.teacherByTId?.userByUId?.uUserName,
            password: getDecryptedDataWithSecretKey(teacherByIdDetails?.teacherByTId?.userByUId?.uUserPassword).originalText
        };
        // Compare teacherName, username, and password
        if (data.teacherName !== initialValues.teacherName) return true;
        if (data.username !== initialValues.username) return true;
        if (data.password !== initialValues.password) return true;

        // Compare the subjects array
        const dataSubjects = data.subjects;
        const initialValuesSubjects = initialValues.subjects;

        if (dataSubjects.length !== initialValuesSubjects.length) return true;

        for (let i = 0; i < dataSubjects.length; i++) {
            if (dataSubjects[i].subject !== initialValuesSubjects[i].subject) return true;
            if (dataSubjects[i].isPrimary !== initialValuesSubjects[i].isPrimary) return true;
        }

        return false;
    }


    const { handleSubmit, setFocus, reset, formState: { errors } } = methods;
    const onSubmit: SubmitHandler<IFormInput> = data => {
        // Filter subjects with non-empty names
        const filteredSubjects = data.subjects.filter(subject => subject.subject.trim() !== '');

        // Prepare variables for createTeachers mutation
        const variables = {
            input: {
                teacherName: data.teacherName.trim(),
                subjects: filteredSubjects.map(subject => ({
                    subjectName: subject.subject.trim(),
                    isPrimary: subject.isPrimary
                })),
                userName: data.username.trim(),
                passWord: data.password.trim(),
                teacherAvatarFileName: profileImageURL !== User ? profileImageURL : "",
            }
        };
        if ((clickAction === 'edit') && (editTeacherLoading === false) && ((areValuesDifferent(data)) || ((profileImageURL !== teacherByIdDetails?.teacherByTId?.tAvatarUrl) && (profileImageURL !== User) ))) {
            editTeacher({
                variables:
                {
                    input: {
                        teacherId: teacherByIdDetails?.teacherByTId?.tId,
                        teacherAvatarUrl: profileImageURL !== User ? profileImageURL : "",
                        teacherName: data.teacherName.trim(),
                        subjects: filteredSubjects.map(subject => ({
                            subjectName: subject.subject.trim(),
                            isPrimary: subject.isPrimary
                        })),
                        ...(data?.username && (data?.username !== teacherByIdDetails?.teacherByTId?.userByUId?.uUserName) && { userName: data.username.trim() }),
                        ...(data?.password && (data?.password !== getDecryptedDataWithSecretKey(teacherByIdDetails?.teacherByTId?.userByUId?.uUserPassword).originalText) && { password: data.password.trim() })
                    }
                }
            })
        }else if((clickAction === 'edit') && (editTeacherLoading === false) && (!areValuesDifferent(data))){
            error("There is no changes to update");
        } else if (clickAction === 'add' && loading === false) {
            // Call createTeachers mutation with variables
            createTeachers({
                variables: variables,
            });
        }
    };

    const handleDelete = (teacherId: string) => {
        if (deleteTeacherLoading === false) {
            setOpenConfirmationPopup(true);
        }
    };

    // click yes button in confirmation popup
    const onYesButtonClick = () => {
        setOpenConfirmationPopup(false);
        deleteTeacher({
            variables: {
                input: {
                    teacherId: teacherByIdDetails?.teacherByTId?.tId
                }
            }
        });
    }

    // click no button in confirmation popup
    const onNoButtonClick = () => {
        setOpenConfirmationPopup(false);
    }

    useEffect(() => {
        setFocus('teacherName');
    }, [setFocus]);

    useEffect(() => {
        if (clickAction === 'edit') {
            reset({
                teacherName: teacherByIdDetails?.teacherByTId?.tName,
                subjects: [
                    { subject: teacherByIdDetails?.teacherByTId?.mainSubject?.nodes?.[0]?.subjectBySjId?.sjOriginalName, isPrimary: true },
                    { subject: teacherByIdDetails?.teacherByTId?.NonPrimarySubjects?.nodes?.[0]?.subjectBySjId?.sjOriginalName || '', isPrimary: false },
                    { subject: teacherByIdDetails?.teacherByTId?.NonPrimarySubjects?.nodes?.[1]?.subjectBySjId?.sjOriginalName || '', isPrimary: false },
                    { subject: teacherByIdDetails?.teacherByTId?.NonPrimarySubjects?.nodes?.[2]?.subjectBySjId?.sjOriginalName || '', isPrimary: false },
                    { subject: teacherByIdDetails?.teacherByTId?.NonPrimarySubjects?.nodes?.[3]?.subjectBySjId?.sjOriginalName || '', isPrimary: false },
                ],
                username: teacherByIdDetails?.teacherByTId?.userByUId?.uUserName,
                password: getDecryptedDataWithSecretKey(teacherByIdDetails?.teacherByTId?.userByUId?.uUserPassword).originalText
            });
            setProfileImageURL(teacherByIdDetails?.teacherByTId?.tAvatarUrl ? teacherByIdDetails?.teacherByTId?.tAvatarUrl : User);
            setImageURL(teacherByIdDetails?.teacherByTId?.tAvatarUrl ? baseUrl+ teacherByIdDetails?.teacherByTId?.tAvatarUrl : User);
        }
    }, [clickAction]);

    useEffect(() => {
        if (!isUploadOpen) {
            if (profileImageURL === undefined && clickAction !== 'edit') {
                setProfileImageURL(User);
            }
        }
    }, [isUploadOpen]);
    useEffect(()=>{
            if (profileImageURL === undefined && clickAction !== 'edit') {
                setImageURL(User);
            }
            if(profileImageURL !== User && profileImageURL !== undefined){
                setImageURL(baseUrl+profileImageURL)
            }
    },[profileImageURL])
    return (
        <div className='adminTeacherForm'>
            <div className='adminTeacherFormHeader'>
                <img
                    onClick={() => { setDisplayForm(!displayForm); setisFullView(!isFullView); setClickAction(''); reset(); }}
                    className="backArrow"
                    alt="back-arrow"
                    src={backArrow}
                />
                <div className="formTitle">
                    {
                        clickAction === 'add' ?
                            <label className='formTitleLabel' title='Adding Teacher'>{isMobileScreen ? "Add Teacher" : "Adding Teacher"}</label>
                            :
                            <div className='statusContainer'>
                                <label className='formTitleLabel' title='teacherStatus'>{teacherByIdDetails?.teacherByTId?.tIsActive ? 'Active' : 'Inactive'}</label>
                                <div className='dotStyle' style={{ backgroundColor: teacherByIdDetails?.teacherByTId?.tIsActive ? '#51CD3D' : 'red' }}></div>
                            </div>
                    }
                </div>
            </div>
            <div className="adminTeacherFormContainer">
                <FormProvider {...methods}>
                    <form className="addTeacherForm" onSubmit={handleSubmit(onSubmit)}>
                        <div className={`addTeacherParentContainer ${Object.keys(errors).length ? 'hasErrorStyle' : ''}`}>
                            <div>
                            <div className="teacherProfileImage">
                                <div className={`${ImageURL ? 'teacherEditContainer' : 'teacherImageContainer'}`}>
                                    {ImageURL && <img className={`${ImageURL ? 'teacherImageProfileStyle' : 'teacherImageDefault'}`} alt='TeacherImage' src={ImageURL} />}
                                </div>
                                <div onClick={() => setIsUploadOpen(true)} className="uploadPhoto">
                                    <span>Upload photo</span>
                                    <img
                                        className="uploadButton"
                                        alt="plus"
                                        src={solidinterfaceplus}
                                    />
                                </div>
                                {
                                    isUploadOpen ?
                                        <FileUpload type='profile' setUrl={setProfileImageURL} open={isUploadOpen} setOpen={setIsUploadOpen} folderName="images" />
                                        : null
                                }
                            </div>
                            <div className="teacherDetails">
                                <div className="teacherNameField">
                                    <label className="nameOfTeacher">Name of Teacher</label>
                                    <InputField label="Name" name="teacherName" classname="subjectStyle" type="text" size="addTeacherField" />
                                </div>
                                <div className="teacherSubjectField">
                                    <label className="teacherSubjectLabel">Main Subject</label>
                                    <SubjectsInput />
                                </div>
                                <div className="teacherLoginAccessField">
                                    <label className="loginAccessLabel">Login Access</label>
                                    <div className="loginField">
                                        <InputField label="Username" name="username" type="text" size="addTeacherField" />
                                        <InputField label="Password" name="password" type="password" size="addTeacherField" />
                                    </div>
                                </div>
                                    {isMobileScreen && (
                                        clickAction !== 'edit' ?
                                            <ButtonStyle paddingLeft='16px' paddingRight='15px' width={'62px'} height={'35px'} color='var(--color-black)' bgcolor="white" variant='outlined' text="Clear" onClick={() => { reset(); setImageURL(User) }} type="reset" />
                                            : <ButtonStyle paddingLeft='16px' paddingRight='15px' width={'62px'} height={'35px'} color='var(--color-black)' bgcolor="white" variant='outlined' disableButton={deleteTeacherLoading} onClick={() => handleDelete(teacherByIdDetails?.getTeacherById?.tId)} deleteBtn={<img src={teacherDeleteIcon} alt="deleteIcon" className='deleteIconStyle' />} />
                                    )}
                            </div>
                            </div>
                            <div className="teacherFormButton">
                                { !isMobileScreen && (
                                    clickAction !== 'edit' ?
                                        <ButtonStyle paddingLeft='16px' paddingRight='15px' width={'62px'} height={'23.92px'} color='var(--color-black)' bgcolor="#EBEBEB" variant='outlined' text="Clear" onClick={() => { reset(); setImageURL(User)}} type="reset" />
                                        : <ButtonStyle paddingLeft='16px' paddingRight='15px' width={'62px'} height={'23.92px'} color='var(--color-black)' bgcolor="#EBEBEB" variant='outlined' disableButton={deleteTeacherLoading} onClick={() => handleDelete(teacherByIdDetails?.getTeacherById?.tId)} deleteBtn={<img src={teacherDeleteIcon} alt="deleteIcon" className='deleteIconStyle' />} />
                                )}
                                <ButtonStyle paddingLeft='26px' paddingRight='28px' width={isMobileScreen ? '100%' : '83.25px'} height={isMobileScreen ? '35px':'23.92px'} disableButton={loading || editTeacherLoading} text="Save" type="submit" />
                            </div>
                        </div>
                    </form>
                    <ConfirmationPopup
                        open={openConfirmationPopup}
                        message={`Are you sure you want to Delete this teacher?`}
                        gifData={DeleteGif}
                        yesButtonFunctionality={onYesButtonClick}
                        noButtonFunctionality={onNoButtonClick}
                        messageWidth={'195px'}
                    />
                </FormProvider>
            </div>
        </div>
    );
};

export default AdminTeacherForm;
